import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { Link, useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { EyeIcon, EyeOffIcon, ArrowLeftIcon, CheckCircleIcon } from '@untitledui-icons/react/line'
import { AuthLayout } from '@/components/auth'
import { useAuth } from '@/hooks/api'
import { resetPasswordSchema, type ResetPasswordFormValues } from '@/schemas/auth'
import { getErrorMessage } from '@/lib/api'

export function ResetPasswordPage() {
  const { t } = useTranslation()
  const [searchParams] = useSearchParams()
  const token = searchParams.get('token')
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const [success, setSuccess] = useState(false)

  const { resetPassword, isResettingPassword, resetPasswordError } = useAuth()

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<ResetPasswordFormValues>({
    resolver: zodResolver(resetPasswordSchema),
    defaultValues: {
      newPassword: '',
      confirmPassword: '',
    },
  })

  const onSubmit = async (data: ResetPasswordFormValues) => {
    if (!token) return

    resetPassword(
      {
        token,
        new_password: data.newPassword,
      },
      {
        onSuccess: () => setSuccess(true),
      }
    )
  }

  // Invalid token state
  if (!token) {
    return (
      <AuthLayout
        title={t('app.auth.invalid_reset_token')}
        subtitle={t('app.auth.errors.INVALID_RESET_TOKEN')}
      >
        <div className="space-y-6">
          <div className="flex justify-center">
            <div className="flex h-14 w-14 items-center justify-center rounded-full bg-error-100 dark:bg-error-900/30">
              <svg
                className="h-7 w-7 text-error-600 dark:text-error-400"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                />
              </svg>
            </div>
          </div>

          <Link
            to="/forgot-password"
            className="
              flex w-full items-center justify-center gap-2 rounded-lg
              bg-brand-500 px-4 py-2.5 text-sm font-semibold text-white
              transition-colors hover:bg-brand-600
            "
          >
            {t('app.auth.request_new_activation_link')}
          </Link>

          <Link
            to="/login"
            className="flex items-center justify-center gap-2 text-sm font-medium text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-200"
          >
            <ArrowLeftIcon className="h-4 w-4" />
            {t('app.auth.back_to_login')}
          </Link>
        </div>
      </AuthLayout>
    )
  }

  // Success state
  if (success) {
    return (
      <AuthLayout
        title={t('app.auth.password_reset_success')}
        subtitle={t('app.auth.go_to_login')}
      >
        <div className="space-y-6">
          <div className="flex justify-center">
            <div className="flex h-14 w-14 items-center justify-center rounded-full bg-success-100 dark:bg-success-900/30">
              <CheckCircleIcon className="h-7 w-7 text-success-600 dark:text-success-400" />
            </div>
          </div>

          <Link
            to="/login"
            className="
              flex w-full items-center justify-center gap-2 rounded-lg
              bg-brand-500 px-4 py-2.5 text-sm font-semibold text-white
              transition-colors hover:bg-brand-600
            "
          >
            {t('app.auth.sign_in')}
          </Link>
        </div>
      </AuthLayout>
    )
  }

  return (
    <AuthLayout
      title={t('app.auth.reset_password')}
      subtitle={t('app.message.password.reset')}
    >
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
        {/* New password field */}
        <div>
          <label
            htmlFor="newPassword"
            className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300"
          >
            {t('app.auth.new_password')}
          </label>
          <div className="relative">
            <input
              id="newPassword"
              type={showPassword ? 'text' : 'password'}
              autoComplete="new-password"
              placeholder={t('app.auth.new_password')}
              className={`
                w-full rounded-lg border px-3.5 py-2.5 pr-10 text-sm
                placeholder:text-gray-400
                focus:outline-none focus:ring-2 focus:ring-brand-500 focus:ring-offset-0
                disabled:cursor-not-allowed disabled:bg-gray-50 disabled:text-gray-500
                dark:bg-gray-800 dark:text-white dark:placeholder:text-gray-500
                ${
                  errors.newPassword
                    ? 'border-error-500 focus:ring-error-500'
                    : 'border-gray-300 dark:border-gray-600'
                }
              `}
              disabled={isResettingPassword}
              {...register('newPassword')}
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              tabIndex={-1}
            >
              {showPassword ? (
                <EyeOffIcon className="h-5 w-5" />
              ) : (
                <EyeIcon className="h-5 w-5" />
              )}
            </button>
          </div>
          {errors.newPassword && (
            <p className="mt-1.5 text-sm text-error-500">
              {errors.newPassword.message}
            </p>
          )}
          <p className="mt-1.5 text-xs text-gray-500 dark:text-gray-400">
            {t('app.message.password.create')}
          </p>
        </div>

        {/* Confirm password field */}
        <div>
          <label
            htmlFor="confirmPassword"
            className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300"
          >
            {t('app.auth.confirm_password')}
          </label>
          <div className="relative">
            <input
              id="confirmPassword"
              type={showConfirmPassword ? 'text' : 'password'}
              autoComplete="new-password"
              placeholder={t('app.auth.confirm_password')}
              className={`
                w-full rounded-lg border px-3.5 py-2.5 pr-10 text-sm
                placeholder:text-gray-400
                focus:outline-none focus:ring-2 focus:ring-brand-500 focus:ring-offset-0
                disabled:cursor-not-allowed disabled:bg-gray-50 disabled:text-gray-500
                dark:bg-gray-800 dark:text-white dark:placeholder:text-gray-500
                ${
                  errors.confirmPassword
                    ? 'border-error-500 focus:ring-error-500'
                    : 'border-gray-300 dark:border-gray-600'
                }
              `}
              disabled={isResettingPassword}
              {...register('confirmPassword')}
            />
            <button
              type="button"
              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              tabIndex={-1}
            >
              {showConfirmPassword ? (
                <EyeOffIcon className="h-5 w-5" />
              ) : (
                <EyeIcon className="h-5 w-5" />
              )}
            </button>
          </div>
          {errors.confirmPassword && (
            <p className="mt-1.5 text-sm text-error-500">
              {errors.confirmPassword.message}
            </p>
          )}
        </div>

        {/* Error message */}
        {resetPasswordError && (
          <div className="rounded-lg bg-error-50 p-3 text-sm text-error-700 dark:bg-error-900/20 dark:text-error-400">
            {getErrorMessage(resetPasswordError)}
          </div>
        )}

        {/* Submit button */}
        <button
          type="submit"
          disabled={isResettingPassword}
          className="
            flex w-full items-center justify-center gap-2 rounded-lg
            bg-brand-500 px-4 py-2.5 text-sm font-semibold text-white
            transition-colors hover:bg-brand-600
            focus:outline-none focus:ring-2 focus:ring-brand-500 focus:ring-offset-2
            disabled:cursor-not-allowed disabled:opacity-50
          "
        >
          {isResettingPassword ? (
            <>
              <svg
                className="h-4 w-4 animate-spin"
                viewBox="0 0 24 24"
                fill="none"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                />
              </svg>
              {t('resources.generic.loading')}
            </>
          ) : (
            t('app.auth.reset_password')
          )}
        </button>

        {/* Back to login */}
        <Link
          to="/login"
          className="flex items-center justify-center gap-2 text-sm font-medium text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-200"
        >
          <ArrowLeftIcon className="h-4 w-4" />
          {t('app.auth.back_to_login')}
        </Link>
      </form>
    </AuthLayout>
  )
}

export default ResetPasswordPage
